import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingDown, TrendingUp } from 'lucide-react';
import apiClient from '../services/api';

export default function PriceHistoryChart({ productId, productTitle }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (productId) {
      loadHistory();
    }
  }, [productId, days]);

  const loadHistory = async () => {
    try {
      const response = await apiClient.get(`/api/merchant/price-history/product/${productId}`, {
        params: { days }
      });
      
      const historyData = response.data.history || [];
      
      // Format for chart
      const chartData = historyData.map(entry => ({
        date: new Date(entry.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        price: entry.price,
        ci_score: entry.ci_score
      }));
      
      setHistory(chartData);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load price history:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-[#22304A] rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-[#22304A] rounded"></div>
        </div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-8 text-center">
        <TrendingUp className="w-12 h-12 text-[#9CB0C7] mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">No Price History Yet</h3>
        <p className="text-sm text-[#9CB0C7]">Price tracking starts when product is added</p>
      </div>
    );
  }

  // Calculate price trend
  const firstPrice = history[0]?.price || 0;
  const lastPrice = history[history.length - 1]?.price || 0;
  const priceChange = lastPrice - firstPrice;
  const priceChangePercent = ((priceChange / firstPrice) * 100).toFixed(1);
  const isIncreasing = priceChange > 0;

  return (
    <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur overflow-hidden shadow-[0_0_0_1px_rgba(62,241,199,0.06)]">
      <div className="p-6 border-b border-[#22304A]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-white mb-1">Price History</h3>
            {productTitle && <p className="text-sm text-[#9CB0C7]">{productTitle}</p>}
          </div>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60 text-sm"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>

        {/* Price Trend Summary */}
        <div className="flex items-center gap-4">
          <div>
            <div className="text-xs text-[#9CB0C7] mb-1">Current Price</div>
            <div className="text-2xl font-bold text-white">${lastPrice.toFixed(2)}</div>
          </div>
          <div className="flex items-center gap-2">
            {isIncreasing ? (
              <TrendingUp className="w-5 h-5 text-[#F55454]" />
            ) : (
              <TrendingDown className="w-5 h-5 text-[#8CF78E]" />
            )}
            <span className={`text-sm font-semibold ${isIncreasing ? 'text-[#F55454]' : 'text-[#8CF78E]'}`}>
              {isIncreasing ? '+' : ''}{priceChangePercent}%
            </span>
            <span className="text-xs text-[#9CB0C7]">vs {days} days ago</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6">
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={history}>
            <CartesianGrid strokeDasharray="3 3" stroke="#22304A" />
            <XAxis 
              dataKey="date" 
              stroke="#9CB0C7" 
              tick={{fill: '#9CB0C7', fontSize: 12}} 
            />
            <YAxis 
              stroke="#9CB0C7" 
              tick={{fill: '#9CB0C7', fontSize: 12}}
              domain={['auto', 'auto']}
            />
            <Tooltip 
              contentStyle={{
                background: '#0E1426', 
                border: '1px solid #22304A', 
                borderRadius: '8px'
              }} 
              labelStyle={{color: '#9CB0C7'}}
            />
            <Line 
              type="monotone" 
              dataKey="price" 
              stroke="#3EF1C7" 
              strokeWidth={2}
              dot={{ fill: '#3EF1C7', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
